import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, Table, Tooltip } from "antd";
import AdminGemstoneReportService from "../../../../services/reports-AdminGemstone-service";
import { BlobToDownloadFile } from "../../../../helper/commanFunction";

// Admin's click-event log, backed by GetRecentGemstoneAttributeTableList (the
// same unfiltered-when-all-6-attribute-fields-are-empty endpoint already
// proven working in the existing GemstoneClicks.js "Gemstone Charts"-style
// dashboard), rather than the 20R attribute-report endpoint.
const AdminClickEventsTable = ({
  dealerId,
  forDealer,
  dataRangeFrom,
  dataRangeTo,
  selectedGemstoneValue,
  refetchToken,
  title,
}) => {
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortColumnName, setSortColumnName] = useState("date");
  const [sortType, setSortType] = useState("DESC");
  const [searchQuery, setSearchQuery] = useState("");

  const buildInputData = (pageNumber, size, sortCol, sortOrder) => ({
    dealerID: dealerId != null ? dealerId.toString() : "",
    forDealer: forDealer ? forDealer.toString() : "",
    fromDate: dataRangeFrom ? moment(dataRangeFrom).format("YYYY-MM-DD") : "2020-08-17",
    toDate: dataRangeTo ? moment(dataRangeTo).format("YYYY-MM-DD") : "2022-09-15",
    clarity: "",
    gemType: "",
    shape: "",
    color: "",
    size: "",
    certificate: "",
    reporttype: "Global",
    selectedGemstoneValue: selectedGemstoneValue,
    pageNumber: pageNumber || 1,
    pageSize: size || 15,
    sortOrderCol: sortCol || "date",
    sortOrderType: sortOrder || "DESC",
  });

  const fetchData = (pageNumber, size, sortCol, sortOrder) => {
    if (dealerId == null) return;
    setTableLoading(true);
    AdminGemstoneReportService.GetRecentGemstoneAttributeTableList(
      buildInputData(pageNumber, size, sortCol, sortOrder)
    )
      .then((response) => {
        setTableLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          const responseData = response.data.responseData;
          setTotalCount(responseData.totalCount || 0);
          setDataSource(responseData.gemstoneVendorTableChartresp || []);
        } else {
          setTotalCount(0);
          setDataSource([]);
        }
      })
      .catch(() => {
        setTableLoading(false);
        setTotalCount(0);
        setDataSource([]);
      });
  };

  useEffect(() => {
    setPageNo(1);
    setSearchQuery("");
    fetchData(1, pageSize, sortColumnName, sortType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, forDealer, refetchToken]);

  const handleTableChange = (pagination, _filters, sorter) => {
    const tableBody = document.querySelector(".edr-admin-clicks .ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;

    let type = "DESC";
    let column = "date";
    if (sorter.order === "descend") {
      type = "DESC";
      column = sorter.field;
    } else if (sorter.order === "ascend") {
      type = "ASC";
      column = sorter.field;
    }

    setSortType(type);
    setSortColumnName(column);
    setPageNo(pagination.current);
    setPageSize(pagination.pageSize);
    fetchData(pagination.current, pagination.pageSize, column, type);
  };

  const handleExportCSV = () => {
    setCsvLoading(true);
    AdminGemstoneReportService.GetCSVChartForTableList(
      buildInputData(pageNo, pageSize, "date", "asc")
    )
      .then((response) => {
        setCsvLoading(false);
        const fileName = `GemstoneClicks_Report${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .reverse()
          .join("/")}.csv`;
        BlobToDownloadFile(response.data, fileName);
      })
      .catch(() => setCsvLoading(false));
  };

  const handleExportPDF = () => {
    setPdfLoading(true);
    AdminGemstoneReportService.GetPDFChartForTableList(
      buildInputData(pageNo, pageSize, "date", "asc")
    )
      .then((response) => {
        setPdfLoading(false);
        const fileName = `GemstoneClicks_Report${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .reverse()
          .join("/")}.pdf`;
        BlobToDownloadFile(response.data, fileName, "pdf");
      })
      .catch(() => setPdfLoading(false));
  };

  const visibleRows = useMemo(() => {
    if (!searchQuery.trim()) return dataSource;
    const q = searchQuery.trim().toLowerCase();
    return dataSource.filter((row) =>
      Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [dataSource, searchQuery]);

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <div>
          <h3>{title || t("Recent Click Events")}</h3>
          <div className="edr-panel-desc">{t("Individual search log · click a column to sort")}</div>
        </div>
        <div className="edr-pagehead-actions">
          <Button className="ghost-btn" onClick={handleExportPDF} loading={pdfLoading}>
            <i className="fa fa-download"></i> {t("PDF")}
          </Button>
          <Button className="ghost-btn" onClick={handleExportCSV} loading={csvLoading}>
            <i className="fa fa-download"></i> {t("CSV")}
          </Button>
        </div>
      </div>

      <div className="edr-tblbar">
        <input
          className="edr-tblsearch"
          placeholder={t("Search this page: IP, shape, color, clarity…")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="edr-badge">{t("{{count}} rows", { count: totalCount })}</span>
      </div>

      <div className="edr-detail-table edr-admin-clicks">
        <Table
          className="pendjewelreq__tbl"
          scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
          loading={tableLoading}
          columns={[
            { title: <Tooltip title={t("UserIPAddress")}>{t("UserIPAddress")}</Tooltip>, ellipsis: true, dataIndex: "usersIPAddress", sorter: () => {} },
            { title: <Tooltip title={t("Date")}>{t("Date")}</Tooltip>, ellipsis: true, dataIndex: "date", key: "date", defaultSortOrder: "descend", sorter: () => {}, render: (item, row) => moment(row.date).format("MM-DD-YYYY, hh:mm:ss A") },
            { title: <Tooltip title={t("Shape")}>{t("Shape")}</Tooltip>, ellipsis: true, dataIndex: "shape", sorter: () => {} },
            { title: <Tooltip title={t("Size")}>{t("Size")}</Tooltip>, ellipsis: true, dataIndex: "size", sorter: () => {} },
            { title: <Tooltip title={t("Color")}>{t("Color")}</Tooltip>, ellipsis: true, dataIndex: "color", sorter: () => {} },
            { title: <Tooltip title={t("Gem Type")}>{t("Gem Type")}</Tooltip>, ellipsis: true, dataIndex: "gemTypeName", sorter: () => {} },
            { title: <Tooltip title={t("Clarity")}>{t("Clarity")}</Tooltip>, ellipsis: true, dataIndex: "clarity", sorter: () => {} },
            { title: <Tooltip title={t("Certificate")}>{t("Certificate")}</Tooltip>, ellipsis: true, dataIndex: "certificate", sorter: () => {} },
            {
              title: <Tooltip title={t("RetailerPrice")}>{t("RetailerPrice")}</Tooltip>,
              ellipsis: true,
              dataIndex: "retailPrice",
              sorter: () => {},
              render: (row, item) => {
                const v = item.retailPrice || "Call For Price";
                return (
                  <Tooltip title={v}>
                    <span>{v}</span>
                  </Tooltip>
                );
              },
            },
            {
              title: <Tooltip title={t("Cost")}>{t("Cost")}</Tooltip>,
              ellipsis: true,
              dataIndex: "cost",
              sorter: () => {},
              render: (row, item) => {
                const v = item.cost || "Call For Price";
                return (
                  <Tooltip title={v}>
                    <span>{v}</span>
                  </Tooltip>
                );
              },
            },
          ]}
          dataSource={visibleRows}
          pagination={{
            size: "small",
            total: totalCount,
            pageNo: pageNo,
            pageSize: pageSize,
            pageSizeOptions: [10, 15, 20, 50, 100],
            responsive: true,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default AdminClickEventsTable;
